import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toastr } from 'react-redux-toastr';
import { push, replace } from 'react-router-redux';
import type {
	IFeedbackDict,
	IProductFeedbackAttributes,
} from '~/OGMAI/Components/FeedbackSlideout';
import { OGMAIViewTab } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import type { ISelectedFilter } from '~/components/DSX/ProductTable';
import type { DSXSelectOption } from '~/components/DSX/Select';
import { OgmAiOptimizationDisplayType } from '~/domain';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { LocalStorage } from '~/helpers';
import { getSession } from '~/helpers/Session';
import type {
	GetOptimizationDetailsResult,
	GetOptimizationSuggestionsResult,
	InProgressRecalculateOptimizationResult,
	MetaProductGroupingResult,
	MetaProductGroupingSummaryLine,
	OptimizationDetail,
	OptimizationSuggestion,
	OrderGuideFilter,
	ProductFeedbackCreationArgs,
	ProductNetUnits,
	RecalculateOptimizationArgs,
	ReportRequestRow,
	RequestLocationLevelPricingReportArgs,
	RequestOgmAiCombinedReportArgs,
	RequestOgmAiGroupingsReportArgs,
	RequestOgmAiOptimizationsReportArgs,
	SearchMetaProductGroupingArgsV6,
} from '~/ogm-hub';
import {
	MetaProductController,
	MetaProductControllerV4,
	MetaProductGroupingSource,
	MetaProductGroupingStatus,
	OptimizationDetailType,
	OptimizationItemStatus,
	OptimizationStatus,
	OptimizationSuggestionController,
	ProductController,
	ProductDetailsController,
	ReportController,
} from '~/ogm-hub';
import * as SuppliersSlice from '../SuppliersSlice';
import * as EditNetUnitsSlice from './EditNetUnitsSlice';

export enum OgmAiStatus {
	New = 1,
	Staged = 2,
	Invalidated = 3,
}

export enum OgmAiReportType {
	Optimizations = 1,
	Groupings = 2,
	Combined = 3,
}

export const OgmAiStatusOptions = {
	[OgmAiStatus.New]: {
		value: OgmAiStatus.New,
		label: 'New',
	},
	[OgmAiStatus.Staged]: {
		value: OgmAiStatus.Staged,
		label: 'Staged',
	},
	[OgmAiStatus.Invalidated]: {
		value: OgmAiStatus.Invalidated,
		label: 'Invalidated',
	},
};

export const GroupingStatusOptions = {
	[MetaProductGroupingSource.User]: {
		value: MetaProductGroupingSource.User,
		label: 'User',
	},
	[MetaProductGroupingSource.Machine]: {
		value: MetaProductGroupingSource.Machine,
		label: "Duplicate SKU's",
	},
	[MetaProductGroupingSource.Ai]: {
		value: MetaProductGroupingSource.Ai,
		label: 'AI',
	},
};

export const OptimizationDateOptions = {
	[90]: {
		value: 90,
		label: 'Last optimized over 90 days ago',
	},
};

export const GroupedDateOptions = {
	[90]: {
		value: 90,
		label: 'Last grouped over 90 days ago',
	},
};

export enum SuggestionsFilterType {
	OrderGuide = 'Order Guide',
	State = 'State',
	GroupingSource = 'Grouping Source',
	LastOptimizedDate = 'Last Optimized',
	LastGroupedDate = 'Last Grouped',
}

export interface IOgmAiSavings {
	isSavingsPending: boolean;
	newSavings: number;
	newCount: number;
	implementedSavings: number;
	stagedSavings: number;
	stagedCount: number;
	annualProjected90DaySavings: number | null;
	annualProjectedYearSavings: number | null;
}

export interface ISuggestionsState {
	suggestions: OptimizationSuggestion[];
	areSuggestionsPending: boolean;
	isOptimizationDeleting: boolean;
	lastSuggestionsRequestTime: number;
	lastUpdated: string;
	orderGuideOptions: OrderGuideFilter[];
	areOrderGuideOptionsPending: boolean;
	selectedOrderGuides: DSXSelectOption[];
	searchTerm: string;
	optimizationSavings: IOgmAiSavings;
	groupingSavings: IOgmAiSavings;
	suggestionsPage: number;
	groupingsPage: number;
	totalSuggestionsLength: number;
	totalGroupingsLength: number;
	checkedOptimizations: string[];
	checkedGroupings: string[];
	selectedProductKey: string;
	optimizationDetail: OptimizationDetail;
	staleOptimizationDetail: OptimizationDetail;
	areDetailsPending: boolean;
	lastDetailsRequestTime: number;
	areGroupingsPending: boolean;
	lastGroupingsRequestTime: number;
	declinedItems: string[];
	declinedItemsDetails: IProductFeedbackAttributes[];
	isApplyDeclineProcessing: boolean;
	isApplyGroupingsProcessing: boolean;
	invalidatedOptimizationSelected: boolean;
	isFeedbackSlideoutOpen: boolean;
	isFeedbackAccept: boolean;
	existingNetUnits: ProductNetUnits[];
	selectedDisplayType: OgmAiOptimizationDisplayType;
	groupings: MetaProductGroupingSummaryLine[];
	pageSize: number;
	selectedOgmAiStatus: OgmAiStatus | null;
	selectedGroupingSource: MetaProductGroupingSource | null;
	selectedGroupedDateFilter: number | null;
	selectedOptimizedDateFilter: number | null;
	isProcessingUserAction: boolean;
	isRecalculatingOptimization: boolean;
	isApplyingOptimizationToMetaProduct: boolean;
	corpSupplierCode: string | null;
	showFiltersModal: boolean;
	suggestionsFilters: ISelectedFilter[];
}

const initialState: ISuggestionsState = {
	suggestions: [],
	areSuggestionsPending: true,
	isOptimizationDeleting: false,
	lastSuggestionsRequestTime: null,
	lastUpdated: null,
	orderGuideOptions: [],
	areOrderGuideOptionsPending: true,
	selectedOrderGuides: null,
	searchTerm: '',
	optimizationSavings: {
		isSavingsPending: true,
		stagedSavings: null,
		stagedCount: 0,
		implementedSavings: null,
		newSavings: null,
		newCount: 0,
		annualProjected90DaySavings: null,
		annualProjectedYearSavings: null,
	},
	groupingSavings: {
		isSavingsPending: true,
		stagedSavings: null,
		stagedCount: 0,
		implementedSavings: null,
		newSavings: null,
		newCount: 0,
		annualProjected90DaySavings: null,
		annualProjectedYearSavings: null,
	},
	suggestionsPage: 0,
	groupingsPage: 0,
	totalSuggestionsLength: 0,
	totalGroupingsLength: 0,
	checkedOptimizations: [],
	checkedGroupings: [],
	selectedProductKey: '',
	optimizationDetail: null,
	staleOptimizationDetail: null,
	areDetailsPending: false,
	lastDetailsRequestTime: null,
	areGroupingsPending: true,
	lastGroupingsRequestTime: null,
	declinedItems: [],
	declinedItemsDetails: [],
	isApplyDeclineProcessing: false,
	isApplyGroupingsProcessing: false,
	invalidatedOptimizationSelected: false,
	isFeedbackSlideoutOpen: false,
	isFeedbackAccept: false,
	existingNetUnits: [],
	selectedDisplayType: OgmAiOptimizationDisplayType.Optimizations,
	groupings: [],
	pageSize: 40,
	selectedOgmAiStatus: null,
	selectedGroupingSource: null,
	isProcessingUserAction: false,
	isRecalculatingOptimization: false,
	isApplyingOptimizationToMetaProduct: false,
	corpSupplierCode: null,
	showFiltersModal: false,
	suggestionsFilters: [],
	selectedGroupedDateFilter: null,
	selectedOptimizedDateFilter: null,
};

export interface GetProductDetailsParams {
	supplierId: number;
	productNumber: string;
}

interface ResultWithRequestTime<T> {
	result: T;
	requestTime: number;
}

export const getOptimizationDetails = createAsyncThunk<
	ResultWithRequestTime<GetOptimizationDetailsResult>,
	number,
	{ state: IAppState }
>(
	'suggestionsOverview/getOptimizationDetails',
	async (metaProductId, { rejectWithValue }) => {
		try {
			const requestTime = Date.now();
			const result =
				await OptimizationSuggestionController.GetOptimizationDetails({
					metaProductId: metaProductId,
				});
			if (result.ok) {
				return { result: result.value, requestTime };
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const deleteOptimization = createAsyncThunk<
	void,
	number,
	{ state: IAppState }
>(
	'suggestionsOverview/deleteOptimization',
	async (optId, { rejectWithValue }) => {
		try {
			await OptimizationSuggestionController.DeleteOptimization(optId);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const getOrderGuideOptions = createAsyncThunk(
	'suggestionsOverview/getOrderGuideOptions',
	async (_: void, { rejectWithValue }) => {
		try {
			const result =
				await OptimizationSuggestionController.GetOptimizationSuggestionFilters();
			if (result.ok) {
				return result.value;
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const applyOptimizations = createAsyncThunk<
	void,
	void,
	{ state: IAppState }
>(
	'suggestionsOverview/applyOptimizations',
	async (_: void, { rejectWithValue, dispatch, getState }) => {
		try {
			const optimizationIds =
				getState().OGMAISuggestions.checkedOptimizations.map((x) =>
					parseInt(x)
				);

			const result = await OptimizationSuggestionController.ApplyOptimization({
				requests: optimizationIds.map((x) => ({
					optimizationId: x,
					rejectedOptimizationItemIds: [],
				})),
			});

			if (result.ok) {
				dispatch(getOptimizations());
				dispatch(getOptimizationSavings());
				dispatch(replace(`/ogmai/${OGMAIViewTab.suggestedOptimization}`));
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const applyGroupings = createAsyncThunk<
	void,
	void,
	{ state: IAppState }
>(
	'suggestionsOverview/applyGroupings',
	async (_: void, { rejectWithValue, dispatch, getState }) => {
		try {
			const groupingIds = getState().OGMAISuggestions.checkedGroupings.map(
				(x) => parseInt(x)
			);

			const result = await MetaProductController.AcceptAllMetaProductGroup({
				metaProductGroupingIds: groupingIds,
			});

			if (result.ok) {
				dispatch(getGroupings());
				dispatch(getGroupingSavings());
				dispatch(replace(`/ogmai/${OGMAIViewTab.suggestedOptimization}`));
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const promoteOptimizationByMasterItem = createAsyncThunk<
	void,
	{ metaProductId: number; feedback: IFeedbackDict },
	{ state: IAppState }
>(
	'suggestionDetail/promoteOptimizationByMasterItem',
	async (
		{ metaProductId, feedback },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const declinedProducts = getState().OGMAISuggestions.declinedItems;
			const optimizationStatus =
				getState().OGMAISuggestions.optimizationDetail.optimizationStatus;

			const productFeedback = getState()
				.OGMAISuggestions.optimizationDetail.products.filter(
					(x) => x.status !== OptimizationItemStatus.StagingDeclined
				)
				.map<ProductFeedbackCreationArgs>((x) => {
					const hasFeedback = x.productNumber in feedback;
					const supplierProductNumber = x.productNumber;
					const accepted = !hasFeedback;
					const acceptedBrand = hasFeedback
						? !feedback[x.productNumber].declinedBrand
						: true;
					const acceptedDescription = hasFeedback
						? !feedback[x.productNumber].declinedDescription
						: true;
					const acceptedPackSize = hasFeedback
						? !feedback[x.productNumber].declinedPackSize
						: true;
					const acceptedCustomerPreference = hasFeedback
						? !feedback[x.productNumber].declinedCustomerPreference
						: true;
					const otherFeedback = hasFeedback
						? feedback[x.productNumber].otherFeedback
						: '';
					return {
						supplierProductNumber,
						accepted,
						acceptedBrand,
						acceptedDescription,
						acceptedPackSize,
						acceptedCustomerPreference,
						otherFeedback,
					};
				});

			switch (optimizationStatus) {
				case OptimizationStatus.New:
					await OptimizationSuggestionController.StageOptimizationByMasterItem({
						productFeedback,
						metaProductId,
						rejectedReplacementSupplierProductNumbers: declinedProducts,
					});
					break;
				case OptimizationStatus.Staged:
					await OptimizationSuggestionController.ApplyOptimizationByMasterItem({
						productFeedback,
						metaProductId,
						rejectedReplacementSupplierProductNumbers: declinedProducts,
					});
					break;
				default:
					await OptimizationSuggestionController.ApplyOptimizationByMasterItem({
						productFeedback,
						metaProductId,
						rejectedReplacementSupplierProductNumbers: declinedProducts,
					});
			}

			dispatch(push(`/ogmai/${OGMAIViewTab.suggestedOptimization}`));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const declineOptimizationByMasterItem = createAsyncThunk<
	void,
	{ metaProductId: number; feedback: IFeedbackDict },
	{ state: IAppState }
>(
	'suggestionDetail/declineOptimizationByMasterItem ',
	async (
		{ metaProductId, feedback },
		{ dispatch, rejectWithValue, getState }
	) => {
		try {
			await OptimizationSuggestionController.DeclineOptimizationByMasterItem({
				productFeedback: getState()
					.OGMAISuggestions.optimizationDetail.products.filter(
						(x) => x.status !== OptimizationItemStatus.StagingDeclined
					)
					.map<ProductFeedbackCreationArgs>((x) => {
						const hasFeedback = x.productNumber in feedback;
						const supplierProductNumber = x.productNumber;
						const accepted = false;
						const acceptedBrand = hasFeedback
							? !feedback[x.productNumber].declinedBrand
							: true;
						const acceptedDescription = hasFeedback
							? !feedback[x.productNumber].declinedDescription
							: true;
						const acceptedPackSize = hasFeedback
							? !feedback[x.productNumber].declinedPackSize
							: true;
						const acceptedCustomerPreference = hasFeedback
							? !feedback[x.productNumber].declinedCustomerPreference
							: true;
						const otherFeedback = hasFeedback
							? feedback[x.productNumber].otherFeedback
							: '';
						return {
							supplierProductNumber,
							accepted,
							acceptedBrand,
							acceptedDescription,
							acceptedPackSize,
							acceptedCustomerPreference,
							otherFeedback,
						};
					}),
				metaProductId,
			});

			dispatch(push(`/ogmai/${OGMAIViewTab.suggestedOptimization}`));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const exportReport = createAsyncThunk<
	ReportRequestRow,
	OgmAiReportType,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'suggestionsOverview/requestReport',
	async (reportType, { getState, rejectWithValue, dispatch }) => {
		let response = null;
		try {
			const currentState = getState().OGMAISuggestions;
			const blueprintIds = currentState.selectedOrderGuides
				? currentState.selectedOrderGuides.map(
						(selectedOrderGuide) => selectedOrderGuide.value
				  )
				: [];
			const status = currentState.selectedOgmAiStatus;
			const session = getSession();
			const customerId = session.customerId;
			switch (reportType) {
				case OgmAiReportType.Combined: {
					const args: RequestOgmAiCombinedReportArgs = {
						customerId,
						blueprintIds,
						optimizationStatus: convertOgmAiStatusToOptimizationStatus(status),
						groupingStatus: convertOgmAiStatusToGroupingStatus(status),
						searchTerm: currentState.searchTerm,
					};

					response = await unwrapFetch2(
						ReportController.RequestOgmAiCombinedReport(args)
					);
					break;
				}
				case OgmAiReportType.Optimizations: {
					const ogmAiOptimizationsReportRequestArgs: RequestOgmAiOptimizationsReportArgs =
						{
							blueprintIds,
							status: convertOgmAiStatusToOptimizationStatus(status),
							searchTerm: currentState.searchTerm,
						};

					response = await unwrapFetch2(
						ReportController.RequestOgmAiOptimizationsReport(
							ogmAiOptimizationsReportRequestArgs
						)
					);
					break;
				}
				case OgmAiReportType.Groupings: {
					const ogmAiGroupingsReportRequestArgs: RequestOgmAiGroupingsReportArgs =
						{
							customerId,
							blueprintIds,
							searchTerm: currentState.searchTerm,
							status: convertOgmAiStatusToGroupingStatus(status),
						};

					response = await unwrapFetch2(
						ReportController.RequestOgmAiGroupingsReport(
							ogmAiGroupingsReportRequestArgs
						)
					);
					break;
				}
			}

			if (response.errors.length === 0) {
				reportSuccessfullyRequested();
				dispatch(push('/reports'));
			} else {
				unknownErrorRequestingReport();
			}
		} catch (err: any) {
			if (err.message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
				return rejectWithValue(response.errors);
			}
		}
	}
);

export const getOptimizations = createAsyncThunk<
	ResultWithRequestTime<GetOptimizationSuggestionsResult>,
	void,
	{ state: IAppState }
>(
	'suggestionsOverview/getOptimizations',
	async (_: void, { rejectWithValue, getState, dispatch }) => {
		try {
			const requestTime = Date.now();
			const state = getState().OGMAISuggestions;
			const orderGuideIds = state.selectedOrderGuides
				? state.selectedOrderGuides.map(
						(selectedOrderGuide) => selectedOrderGuide.value
				  )
				: null;
			const optimizationStatus = convertOgmAiStatusToOptimizationStatus(
				state.selectedOgmAiStatus
			);
			const searchTerm = state.searchTerm;
			const page = state.suggestionsPage + 1;
			const date = new Date();
			date.setDate(
				date.getDate() -
					(state.selectedOptimizedDateFilter
						? state.selectedOptimizedDateFilter
						: 0)
			);
			const notOptimizedAfter = state.selectedOptimizedDateFilter
				? date.toISOString()
				: null;
			const result =
				await OptimizationSuggestionController.GetOptimizationSuggestions({
					orderGuideIds,
					searchTerm,
					status: optimizationStatus,
					pageIndex: page,
					notOptimizedAfter: notOptimizedAfter,
				});
			dispatch(SuppliersSlice.fetchCurrentCustomerSuppliers());
			if (result.ok) {
				return { result: result.value, requestTime };
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const getOptimizationSavings = createAsyncThunk(
	'suggestionsOverview/getOptimizationSavings',
	async (_: void, { rejectWithValue }) => {
		try {
			const result =
				await OptimizationSuggestionController.GetOptimizationSavings();
			if (result.ok) {
				return result.value;
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const getGroupingSavings = createAsyncThunk(
	'suggestionsOverview/getGroupingSavings',
	async (_: void, { rejectWithValue }) => {
		try {
			const result = await MetaProductControllerV4.GetGroupingSavings();
			if (result.ok) {
				return result.value;
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const requestLocationLevelPricingReport = createAsyncThunk<
	void,
	{ corpSupplierCode: string; productNumber: string; um: string },
	{ state: IAppState }
>(
	'productSearchPage/requestLocationLevelPricingReport',
	async (
		args: { corpSupplierCode: string; productNumber: string; um: string },
		{ getState }
	) => {
		try {
			const state = getState();
			const supplier =
				state.suppliers.customerSuppliersByCode[args.corpSupplierCode];
			const params: RequestLocationLevelPricingReportArgs = {
				supplierId: supplier?.id,
				supplierProductNumber: args.productNumber,
				unitOfMeasure: args.um,
			};
			const result = await unwrapFetch2(
				ReportController.RequestLocationLevelPricingReport(params)
			);
			if (result.errors.length === 0) {
				reportSuccessfullyRequested();
			} else {
				unknownErrorRequestingReport();
			}
		} catch (err: any) {
			if (err.message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
			}
		}
	}
);

export const onDeclineItem = createAsyncThunk<
	{ details: IProductFeedbackAttributes; item: string },
	GetProductDetailsParams,
	{ state: IAppState }
>(
	'suggestionsDetails/onDeclineItem',
	async (params: GetProductDetailsParams, { rejectWithValue, getState }) => {
		try {
			const { supplierId, productNumber } = params;
			const corpSupplierCode = getState().OGMAISuggestions.corpSupplierCode;
			const { results: serachResult } = await unwrapFetch2(
				ProductController.GetProductsByProductNumbersAnyStatus(supplierId, [
					productNumber,
				])
			);
			const generalDetails = serachResult[0];
			const extraDetails = await unwrapFetch2(
				ProductDetailsController.GetProductDetails(
					corpSupplierCode,
					productNumber
				)
			);

			const description = generalDetails.extendedDescription
				? generalDetails.extendedDescription
				: generalDetails.description;

			const imageWidth = '300';
			const imageHeight = '300';
			const imageString = extraDetails.images.filter(
				(e) => e !== '' && e !== null
			)[0];
			const image = imageString
				? `${imageString}?h=${imageHeight}&w=${imageWidth}`
				: '/images/image-not-found.png';

			const details: IProductFeedbackAttributes = {
				number: productNumber,
				supplierId,
				description,
				packSize: generalDetails.packSize,
				brand: generalDetails.brand,
				image,
				imageHeight,
				imageWidth,
			};

			return {
				details,
				item: productNumber,
			};
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const onCheckFeedbackAccept = createAsyncThunk<
	void,
	number,
	{ state: IAppState }
>(
	'suggestionDetails/feedbackAccept',
	async (metaProductId: number, { dispatch, getState }) => {
		if (getState().OGMAISuggestions.declinedItems.length !== 0) {
			dispatch(OGMAISuggestionsSlice.actions.setIsFeedbackAccept(true));
			dispatch(OGMAISuggestionsSlice.actions.onOpenProductFeedbackSlideout());
		} else {
			dispatch(
				promoteOptimizationByMasterItem({ metaProductId, feedback: {} })
			);
		}
	}
);

export const onCheckFeedbackDecline = createAsyncThunk<
	void,
	string | undefined,
	{ state: IAppState }
>('suggestionDetails/feedbackDecline', async (_, { dispatch, getState }) => {
	// When declining we need to get details for all product additions before opening the slideout
	const declinedItems = getState().OGMAISuggestions.declinedItems;
	const itemsToDecline =
		getState().OGMAISuggestions.optimizationDetail.products.filter(
			(p) =>
				p.type === OptimizationDetailType.New &&
				!declinedItems.includes(p.productNumber)
		); // don't double add
	itemsToDecline.forEach((p) =>
		dispatch(
			onDeclineItem({
				productNumber: p.productNumber,
				supplierId: p.supplierId,
			})
		)
	);
	dispatch(OGMAISuggestionsSlice.actions.setIsFeedbackAccept(false));
	if (itemsToDecline.length !== 0 || declinedItems.length !== 0) {
		dispatch(OGMAISuggestionsSlice.actions.onOpenProductFeedbackSlideout());
	}
});

export const getGroupings = createAsyncThunk<
	ResultWithRequestTime<MetaProductGroupingResult>,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'suggestionDetails/getGroupings',
	async (_: void, { getState, rejectWithValue }) => {
		try {
			const requestTime = Date.now();
			const requestArgs = getGroupingSearchArgs(null, getState);
			const result = await unwrapFetch2(
				MetaProductController.SearchMetaProductGroupings(requestArgs)
			);
			return { result: result, requestTime };
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const recalculateOptimization = createAsyncThunk<
	void,
	RecalculateOptimizationArgs,
	{ state: IAppState }
>(
	'suggestionDetails/recalculateOptimization',
	async (args: RecalculateOptimizationArgs, { rejectWithValue }) => {
		try {
			await OptimizationSuggestionController.RecalculateOptimization(
				args.metaProductId,
				args
			);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const getInProgressRecalculation = createAsyncThunk<
	InProgressRecalculateOptimizationResult,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'suggestionDetails/getInProgressRecalculation',
	async (masterItemId: number, { rejectWithValue }) => {
		try {
			const result = unwrapFetch2(
				OptimizationSuggestionController.GetInProgressRecalculation(
					masterItemId
				)
			);
			return result;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const onCheckOptimizations = createAsyncThunk<
	void,
	string[],
	{ state: IAppState }
>(
	'suggestionsOverview/onCheckOptimizations',
	async (checkedOptimizationNumbers: string[], { getState, dispatch }) => {
		dispatch(
			OGMAISuggestionsSlice.actions.setCheckedOptimizations(
				checkedOptimizationNumbers
			)
		);

		const optimizationSuggestions = getState().OGMAISuggestions.suggestions;
		const checkedOptimizationSuggestions = optimizationSuggestions.filter(
			(x) =>
				getState().OGMAISuggestions.checkedOptimizations.includes(
					x.optimizationId.toString()
				) && x.status == OptimizationStatus.Invalidated
		);
		dispatch(
			OGMAISuggestionsSlice.actions.setIsInvalidatedOptimizationSelected(
				checkedOptimizationSuggestions.length > 0
			)
		);
	}
);

const getGroupingSearchArgs = (masterItemId: number | null, getState) => {
	const state = getState().OGMAISuggestions;
	const customerId = LocalStorage.getCustomerId();
	const orderGuideIds =
		state.selectedOrderGuides && state.selectedOrderGuides.length > 0
			? state.selectedOrderGuides.map(
					(selectedOrderGuide) => selectedOrderGuide.value
			  )
			: null;
	const statusIds =
		state.selectedOgmAiStatus === null ||
		state.selectedOgmAiStatus === OgmAiStatus.Invalidated
			? [MetaProductGroupingStatus.New, MetaProductGroupingStatus.Staged]
			: [convertOgmAiStatusToGroupingStatus(state.selectedOgmAiStatus)];

	const sourceIds = state.selectedGroupingSource
		? [state.selectedGroupingSource]
		: [MetaProductGroupingSource.Machine, MetaProductGroupingSource.Ai];
	const date = new Date();
	date.setDate(
		date.getDate() -
			(state.selectedGroupedDateFilter ? state.selectedGroupedDateFilter : 0)
	);
	const notGroupedAfter = state.selectedGroupedDateFilter
		? date.toISOString()
		: null;
	const pageIndex = state.groupingsPage * state.pageSize;
	const requestArgs: SearchMetaProductGroupingArgsV6 = {
		metaProductIds: masterItemId ? [masterItemId] : null,
		statusIds: statusIds,
		sourceIds: sourceIds,
		startDate: null,
		endDate: null,
		customerId: customerId,
		blueprintIds: orderGuideIds,
		distributionCenterId: null,
		searchTerm: state.searchTerm,
		pageSize: state.pageSize,
		pageIndex: pageIndex,
		notGroupedAfter: notGroupedAfter,
	};

	return requestArgs;
};

const convertOgmAiStatusToOptimizationStatus = (ogmAiStatus: OgmAiStatus) => {
	switch (ogmAiStatus) {
		case OgmAiStatus.New:
			return OptimizationStatus.New;
		case OgmAiStatus.Staged:
			return OptimizationStatus.Staged;
		case OgmAiStatus.Invalidated:
			return OptimizationStatus.Invalidated;
		default:
			return null;
	}
};

const convertOgmAiStatusToGroupingStatus = (ogmAiStatus: OgmAiStatus) => {
	switch (ogmAiStatus) {
		case OgmAiStatus.New:
			return MetaProductGroupingStatus.New;
		case OgmAiStatus.Staged:
			return MetaProductGroupingStatus.Staged;
		default:
			return null;
	}
};

const unknownErrorRequestingReport = () => {
	toastr.error(
		'Report Request Failed',
		'The report was not successfully requested.'
	);
};

const reportCurrentlyInProgress = () => {
	toastr.warning(
		'Report Request Failed',
		'Another report request is currently in progress'
	);
};

const reportSuccessfullyRequested = () => {
	toastr.success(
		'Report Request Sent',
		'The report was successfully requested.'
	);
};

export const generateNetUnitEditRequestKey = (
	corpSupplierCode: string,
	supplierProductNumber: string,
	unitMeasureCode: string
) => {
	return `${corpSupplierCode}~${supplierProductNumber}~${unitMeasureCode}`;
};

export const OGMAISuggestionsSlice = createSlice({
	name: 'SuggestionOverview',
	initialState,
	reducers: {
		selectedProductKeyChange: (state, action) => {
			state.selectedProductKey = action.payload;
		},
		setSelectedOrderGuides: (state, action) => {
			state.selectedOrderGuides = action.payload;
		},
		setSelectedOgmAiStatus: (state, action) => {
			state.selectedOgmAiStatus = action.payload;
		},
		setSelectedGroupingSource: (state, action) => {
			state.selectedGroupingSource = action.payload;
		},
		setSelectedGroupedDateFilter: (state, action) => {
			state.selectedGroupedDateFilter = action.payload;
		},
		setSelectedOptimizedDateFilter: (state, action) => {
			state.selectedOptimizedDateFilter = action.payload;
		},
		onSearchTermChange: (state, action) => {
			state.searchTerm = action.payload;
		},
		onPageChange: (state, action) => {
			if (
				state.selectedDisplayType === OgmAiOptimizationDisplayType.Optimizations
			) {
				state.suggestionsPage = action.payload;
			} else {
				state.groupingsPage = action.payload;
			}
		},
		setCheckedOptimizations: (state, action) => {
			state.checkedOptimizations = action.payload;
		},
		setIsInvalidatedOptimizationSelected: (state, action) => {
			state.invalidatedOptimizationSelected = action.payload;
		},
		onCheckGroupings: (state, action) => {
			state.checkedGroupings = action.payload;
		},
		clearOptimizationDetails: (state) => {
			state.optimizationDetail = null;
			state.checkedOptimizations = [];
			state.declinedItems = [];
			state.declinedItemsDetails = [];
		},
		onOpenProductFeedbackSlideout: (state) => {
			state.isFeedbackSlideoutOpen = true;
		},
		onCancelProductFeedbackSlideout: (state) => {
			state.isFeedbackSlideoutOpen = false;
		},
		onRevertDeclinedItems: (state) => {
			state.declinedItems = [];
			state.declinedItemsDetails = [];
		},
		setIsFeedbackAccept: (state, action) => {
			state.isFeedbackAccept = action.payload;
		},
		onDisplayTypeChange: (state, action) => {
			state.selectedDisplayType = action.payload;
		},
		toggleFiltersModal: (state) => {
			state.showFiltersModal = !state.showFiltersModal;
		},
		addFilter: (state, action) => {
			state.suggestionsFilters.push({
				label: action.payload.filterType,
				value: action.payload.value,
			});
		},
		clearFilter: (state, action) => {
			const filterToRemove = state.suggestionsFilters.find(
				(f) => f.label === action.payload.label
			);
			const indexToRemove = state.suggestionsFilters.indexOf(filterToRemove);
			state.suggestionsFilters.splice(indexToRemove, 1);
		},
		clearFilters: (state) => {
			state.suggestionsFilters = [];
		},
	},
	extraReducers: (builder) => {
		builder.addCase(onDeclineItem.fulfilled, (state, action) => {
			state.declinedItems.push(action.payload.item);
			state.declinedItemsDetails.push(action.payload.details);
		});
		builder.addCase(getOptimizationDetails.pending, (state) => {
			state.optimizationDetail = null;
			state.staleOptimizationDetail = null;
			state.checkedOptimizations = [];
			state.declinedItems = [];
			state.declinedItemsDetails = [];
			state.areDetailsPending = true;
			state.corpSupplierCode = initialState.corpSupplierCode;
		});
		builder.addCase(deleteOptimization.pending, (state) => {
			state.isOptimizationDeleting = true;
		});
		builder.addCase(deleteOptimization.fulfilled, (state) => {
			state.isOptimizationDeleting = false;
			state.optimizationDetail = null;
			state.staleOptimizationDetail = null;
		});
		builder.addCase(deleteOptimization.rejected, (state) => {
			state.isOptimizationDeleting = false;
		});
		builder.addCase(getOptimizationDetails.fulfilled, (state, action) => {
			state.optimizationDetail = action.payload.result.optimizationDetails;
			state.staleOptimizationDetail =
				action.payload.result.staleOptimizationDetails;
			state.existingNetUnits =
				action.payload.result.optimizationDetails?.netUnitProducts;
			state.areDetailsPending = false;
			state.lastDetailsRequestTime = action.payload.requestTime;
			EditNetUnitsSlice.initNetUnits(
				action.payload.result.optimizationDetails?.netUnitProducts
			);
			state.corpSupplierCode =
				action.payload.result.optimizationDetails?.corpSupplierCode;
		});
		builder.addCase(getOptimizationDetails.rejected, (state) => {
			state.optimizationDetail = null;
			state.staleOptimizationDetail = null;
			state.checkedOptimizations = [];
			state.declinedItems = [];
			state.declinedItemsDetails = [];
			state.areDetailsPending = true;
			state.corpSupplierCode = initialState.corpSupplierCode;
		});
		builder.addCase(getOrderGuideOptions.pending, (state) => {
			state.areOrderGuideOptionsPending = true;
		});
		builder.addCase(getOrderGuideOptions.fulfilled, (state, action) => {
			state.areOrderGuideOptionsPending = false;

			state.orderGuideOptions = action.payload.orderGuideFilters.map(
				(orderGuideFilter) => ({
					id: orderGuideFilter.id,
					name: orderGuideFilter.name,
				})
			);
		});
		builder.addCase(getOrderGuideOptions.rejected, (state) => {
			state.orderGuideOptions = [];
			state.selectedOrderGuides = null;
		});
		builder.addCase(getOptimizations.pending, (state) => {
			state.areSuggestionsPending = true;
		});
		builder.addCase(getOptimizations.fulfilled, (state, action) => {
			if (state.lastSuggestionsRequestTime <= action.payload.requestTime) {
				state.suggestions = action.payload.result.suggestions;
				state.lastUpdated = action.payload.result.lastUpdated;
				state.areSuggestionsPending = false;
				state.totalSuggestionsLength = action.payload.result.totalResults;
				state.lastSuggestionsRequestTime = action.payload.requestTime;

				if (
					state.suggestionsPage > initialState.suggestionsPage &&
					action.payload.result.suggestions.length === 0
				) {
					state.suggestionsPage = initialState.suggestionsPage;
				}
			}
		});
		builder.addCase(getGroupings.pending, (state) => {
			state.areGroupingsPending = true;
		});
		builder.addCase(getGroupings.fulfilled, (state, action) => {
			if (state.lastGroupingsRequestTime <= action.payload.requestTime) {
				state.groupings = action.payload.result.results;
				state.lastUpdated = action.payload.result.lastUpdated;
				state.areGroupingsPending = false;
				state.totalGroupingsLength = action.payload.result.totalResults;
				state.lastGroupingsRequestTime = action.payload.requestTime;

				if (
					state.groupingsPage > initialState.groupingsPage &&
					action.payload.result.results.length === 0
				) {
					state.groupingsPage = initialState.groupingsPage;
				}
			}
		});
		builder.addCase(getOptimizations.rejected, (state) => {
			state.suggestions = [];
			state.lastUpdated = null;
		});
		builder.addCase(getOptimizationSavings.pending, (state) => {
			state.optimizationSavings = initialState.optimizationSavings;
		});
		builder.addCase(getOptimizationSavings.fulfilled, (state, action) => {
			state.optimizationSavings.isSavingsPending = false;
			state.optimizationSavings.newSavings = action.payload.totalNewSavings;
			state.optimizationSavings.newCount = action.payload.newCount;
			state.optimizationSavings.stagedSavings =
				action.payload.totalStagedSavings;
			state.optimizationSavings.stagedCount = action.payload.stagedCount;
			state.optimizationSavings.implementedSavings =
				action.payload.totalImplementedSavings;
			state.optimizationSavings.annualProjected90DaySavings =
				action.payload.annualProjected90DaySavings;
			state.optimizationSavings.annualProjectedYearSavings =
				action.payload.annualProjectedYearSavings;
		});
		builder.addCase(getOptimizationSavings.rejected, (state) => {
			state.optimizationSavings.isSavingsPending = false;
			state.optimizationSavings.newSavings = 0;
			state.optimizationSavings.stagedSavings = 0;
			state.optimizationSavings.implementedSavings = 0;
			state.optimizationSavings.annualProjected90DaySavings = 0;
			state.optimizationSavings.annualProjectedYearSavings = 0;
		});
		builder.addCase(getGroupingSavings.pending, (state) => {
			state.groupingSavings = initialState.groupingSavings;
		});
		builder.addCase(getGroupingSavings.fulfilled, (state, action) => {
			state.groupingSavings.isSavingsPending = false;
			state.groupingSavings.newSavings = action.payload.totalNewSavings;
			state.groupingSavings.stagedSavings = action.payload.totalStagedSavings;
			state.groupingSavings.implementedSavings =
				action.payload.totalImplementedSavings;
		});
		builder.addCase(getGroupingSavings.rejected, (state) => {
			state.groupingSavings.isSavingsPending = false;
			state.groupingSavings.newSavings = 0;
			state.groupingSavings.stagedSavings = 0;
			state.groupingSavings.implementedSavings = 0;
		});
		builder.addCase(applyOptimizations.fulfilled, (state) => {
			state.checkedOptimizations = [];
			state.declinedItems = [];
			state.declinedItemsDetails = [];
			state.optimizationDetail = null;
			state.isApplyDeclineProcessing = false;
		});
		builder.addCase(applyGroupings.fulfilled, (state) => {
			state.checkedGroupings = [];
			state.isApplyGroupingsProcessing = false;
		});
		builder.addCase(applyGroupings.pending, (state) => {
			state.isApplyGroupingsProcessing = true;
		});
		builder.addCase(applyOptimizations.pending, (state) => {
			state.isApplyDeclineProcessing = true;
		});
		builder.addCase(promoteOptimizationByMasterItem.pending, (state) => {
			state.isApplyDeclineProcessing = true;
			state.isProcessingUserAction = true;
		});
		builder.addCase(promoteOptimizationByMasterItem.fulfilled, (state) => {
			state.declinedItems = [];
			state.declinedItemsDetails = [];
			state.optimizationDetail = null;
			state.checkedOptimizations = [];
			state.isApplyDeclineProcessing = false;
			state.isFeedbackSlideoutOpen = false;
			state.isProcessingUserAction = false;
		});
		builder.addCase(declineOptimizationByMasterItem.pending, (state) => {
			state.isApplyDeclineProcessing = true;
			state.isProcessingUserAction = true;
		});
		builder.addCase(declineOptimizationByMasterItem.fulfilled, (state) => {
			state.declinedItems = [];
			state.declinedItemsDetails = [];
			state.optimizationDetail = null;
			state.checkedOptimizations = [];
			state.isApplyDeclineProcessing = false;
			state.isFeedbackSlideoutOpen = false;
			state.isProcessingUserAction = false;
		});
		builder.addCase(getInProgressRecalculation.fulfilled, (state, action) => {
			state.isRecalculatingOptimization = action.payload.inProgress;
			if (state.isApplyingOptimizationToMetaProduct) {
				state.isApplyingOptimizationToMetaProduct = action.payload.inProgress;
			}
		});
		builder.addCase(recalculateOptimization.fulfilled, (state, action) => {
			state.isRecalculatingOptimization = true;
			state.optimizationDetail = null;
			if (action.meta.arg.applyAutomatically) {
				state.isApplyingOptimizationToMetaProduct = true;
			}
		});
		builder.addCase(applySuggestionsFilters.fulfilled, (state) => {
			state.showFiltersModal = false;
		});
		builder.addCase('@ca:SELECTED_CUSTOMER_SUCCESS', () => initialState);
		builder.addCase(
			'@mpla:METAPRODUCT_LISTING_PAGE_LOADED_SUCCESS',
			() => initialState
		);
	},
});

export const {
	onSearchTermChange,
	onPageChange,
	onCheckGroupings,
	clearOptimizationDetails,
	onOpenProductFeedbackSlideout,
	onRevertDeclinedItems,
	onCancelProductFeedbackSlideout,
	onDisplayTypeChange,
	toggleFiltersModal,
} = OGMAISuggestionsSlice.actions;

export const setSelectedOrderGuideAsync = createAsyncThunk(
	'suggestionsOverview/setSelectedOrderGuide',
	async (option: DSXSelectOption[], { rejectWithValue, dispatch }) => {
		try {
			if (option.length > 0) {
				dispatch(OGMAISuggestionsSlice.actions.setSelectedOrderGuides(option));
			} else {
				dispatch(OGMAISuggestionsSlice.actions.setSelectedOrderGuides([]));
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const setSelectedOgmAiStatusAsync = createAsyncThunk(
	'suggestionsOverview/setSelectedOgmAiStatusAsync',
	async (option: DSXSelectOption, { dispatch, rejectWithValue }) => {
		try {
			if (option) {
				dispatch(
					OGMAISuggestionsSlice.actions.setSelectedOgmAiStatus(option.value)
				);
			} else {
				dispatch(OGMAISuggestionsSlice.actions.setSelectedOgmAiStatus(null));
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const setSelectedGroupingSourceAsync = createAsyncThunk(
	'suggestionsOverview/setSelectedGroupingSourceAsync',
	async (option: DSXSelectOption, { dispatch, rejectWithValue }) => {
		try {
			if (option) {
				dispatch(
					OGMAISuggestionsSlice.actions.setSelectedGroupingSource(option.value)
				);
			} else {
				dispatch(OGMAISuggestionsSlice.actions.setSelectedGroupingSource(null));
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const setSelectedGroupedDateFilterAsync = createAsyncThunk(
	'suggestionsOverview/setSelectedDateFilterAsync',
	async (option: DSXSelectOption, { dispatch, rejectWithValue }) => {
		try {
			if (option) {
				dispatch(
					OGMAISuggestionsSlice.actions.setSelectedGroupedDateFilter(
						option.value
					)
				);
			} else {
				dispatch(
					OGMAISuggestionsSlice.actions.setSelectedGroupedDateFilter(null)
				);
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const setSelectedOptimizedDateFilterAsync = createAsyncThunk(
	'suggestionsOverview/setSelectedDateFilterAsync',
	async (option: DSXSelectOption, { dispatch, rejectWithValue }) => {
		try {
			if (option) {
				dispatch(
					OGMAISuggestionsSlice.actions.setSelectedOptimizedDateFilter(
						option.value
					)
				);
			} else {
				dispatch(
					OGMAISuggestionsSlice.actions.setSelectedOptimizedDateFilter(null)
				);
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const applySuggestionsFilters = createAsyncThunk<
	void,
	void,
	{ state: IAppState }
>(
	'suggestionsOverview/applySuggestionsFilters',
	async (_, { dispatch, rejectWithValue, getState }) => {
		try {
			const state = getState().OGMAISuggestions;
			dispatch(OGMAISuggestionsSlice.actions.clearFilters());
			if (
				state.selectedOrderGuides /*&& state.selectedOrderGuides.length > 0*/
			) {
				const filterValue =
					state.selectedOrderGuides.length > 1
						? `${state.selectedOrderGuides.length} Options Selected`
						: state.selectedOrderGuides[0]?.label;
				dispatch(
					OGMAISuggestionsSlice.actions.addFilter({
						filterType: SuggestionsFilterType.OrderGuide.toString(),
						value: filterValue,
					})
				);
			}

			if (state.selectedOgmAiStatus) {
				dispatch(
					OGMAISuggestionsSlice.actions.addFilter({
						filterType: SuggestionsFilterType.State.toString(),
						value: OgmAiStatusOptions[state.selectedOgmAiStatus].label,
					})
				);
			}

			if (state.selectedGroupingSource) {
				dispatch(
					OGMAISuggestionsSlice.actions.addFilter({
						filterType: SuggestionsFilterType.GroupingSource.toString(),
						value: GroupingStatusOptions[state.selectedGroupingSource].label,
					})
				);
			}

			if (state.selectedGroupedDateFilter) {
				dispatch(
					OGMAISuggestionsSlice.actions.addFilter({
						filterType: SuggestionsFilterType.LastGroupedDate.toString(),
						value: GroupedDateOptions[state.selectedGroupedDateFilter].label,
					})
				);
			}

			if (state.selectedOptimizedDateFilter) {
				dispatch(
					OGMAISuggestionsSlice.actions.addFilter({
						filterType: SuggestionsFilterType.LastOptimizedDate.toString(),
						value:
							OptimizationDateOptions[state.selectedOptimizedDateFilter].label,
					})
				);
			}

			dispatch(getOptimizations());
			dispatch(getGroupings());
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const clearFilter = createAsyncThunk<
	void,
	ISelectedFilter,
	{ state: IAppState }
>(
	'suggestionsOverview/applySuggestionsFilters',
	async (filter: ISelectedFilter, { dispatch, rejectWithValue }) => {
		try {
			dispatch(OGMAISuggestionsSlice.actions.clearFilter(filter));
			dispatch(getOptimizations());
			dispatch(getGroupings());
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);
